import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  slackChannel,
  slackDmsSent,
  slackDraft,
  slackNoteDmSent,
  slackPosted,
  slackState,
} from '@contracts/slack';
import type { SlackNoteDmBody, SlackPostBody } from '@contracts/slack';
import { me } from '@contracts/auth';
import { calendarStatus, eventBrief } from '@contracts/calendar';
import { auditEvent, organization, orgTiles } from '@contracts/orgs';
import { orgAnalytics } from '@contracts/analytics';
import {
  knowledgeDetail,
  knowledgeEntry,
  knowledgeOverview,
  readEverythingResult,
} from '@contracts/knowledge';
import type { KnowledgeQuery, UpdateKnowledgeBody } from '@contracts/knowledge';
import { answer } from '@contracts/ask';
import { discussionDetail, discussionsList } from '@contracts/discussions';
import type { AnswersQuery, AskQuestionBody } from '@contracts/ask';
import { notice } from '@contracts/common';
import { spaceSummary } from '@contracts/spaces';
import { fellowPreview, importRun } from '@contracts/imports';
import type { FellowPreviewBody, StartImportBody } from '@contracts/imports';
import { API_ORIGIN, ApiError, api, isNotSignedIn } from './client';
import { actedOn } from '../ledger/acted-here';
import { withLine, withNewer } from '../lib/live-lines';

export const keys = {
  me: ['me'] as const,
  org: ['org'] as const,
  notices: ['notices'] as const,
  spaces: ['spaces'] as const,
};

/** Who am I. A 401 is a state, not an error: "not signed in". */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api(me, '/v1/auth/me'),
    retry: (count, error) => !isNotSignedIn(error) && count < 2,
    staleTime: 60_000,
  });
}

export function useOrg() {
  return useQuery({ queryKey: keys.org, queryFn: () => api(organization, '/v1/org') });
}

/**
 * Organisation analytics (PRD 34) for a range — administrators only; the
 * server's 404 is a state ("for administrators"), not a failure to retry.
 */
export function useOrgAnalytics(from: string, to: string) {
  return useQuery({
    queryKey: [...keys.org, 'analytics', from, to] as const,
    queryFn: () =>
      api(
        orgAnalytics,
        `/v1/org/analytics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    staleTime: 60_000,
  });
}

// --- knowledge and asking (PRD 30) ---------------------------------------------------

const knowledgeKey = (query: KnowledgeQuery) => ['knowledge', query] as const;

/** The Knowledge page in one read; with `poll`, refetched while the backfill is running. */
export function useKnowledge(query: KnowledgeQuery = {}, options: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: knowledgeKey(query),
    queryFn: () => {
      const params = new URLSearchParams();
      if (query.space) params.set('space', query.space);
      if (query.kind) params.set('kind', query.kind);
      if (query.meeting) params.set('meeting', query.meeting);
      if (query.folder) params.set('folder', query.folder);
      const qs = params.toString();
      return api(knowledgeOverview, `/v1/knowledge${qs ? `?${qs}` : ''}`);
    },
    refetchInterval: (q) =>
      options.poll && q.state.data && q.state.data.reading.unread > 0 ? 5_000 : false,
  });
}

export function useKnowledgeEntry(id: string | null) {
  return useQuery({
    queryKey: ['knowledge', 'entry', id] as const,
    queryFn: () => api(knowledgeDetail, `/v1/knowledge/${id}`),
    enabled: id !== null,
  });
}

export function useKnowledgeDeclined(enabled = true) {
  return useQuery({
    queryKey: ['knowledge', 'declined'] as const,
    queryFn: () => api(z.array(knowledgeEntry), '/v1/knowledge/declined'),
    enabled,
  });
}

export type KnowledgeTap =
  'keep' | 'keep-both' | 'decline' | 'still-true' | 'retire' | 'undo-decline';

/** The taps, each the server's answer; every knowledge read is refetched after. */
export function useKnowledgeActions() {
  const client = useQueryClient();
  const done = () => client.invalidateQueries({ queryKey: ['knowledge'] });
  const tap = useMutation({
    mutationFn: ({ id, tap }: { id: string; tap: KnowledgeTap }) =>
      api(knowledgeDetail, `/v1/knowledge/${id}/${tap}`, { method: 'POST' }),
    onSuccess: done,
  });
  const edit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateKnowledgeBody }) =>
      api(knowledgeDetail, `/v1/knowledge/${id}`, { method: 'PATCH', json: body }),
    onSuccess: done,
  });
  const readEverything = useMutation({
    mutationFn: () =>
      api(readEverythingResult, '/v1/knowledge/read-everything', { method: 'POST' }),
    onSuccess: done,
  });
  const merge = useMutation({
    mutationFn: (body: { into: string; others: string[] }) =>
      api(knowledgeDetail, '/v1/knowledge/merge', { method: 'POST', json: body }),
    onSuccess: done,
  });
  const distinct = useMutation({
    mutationFn: (body: { ids: string[] }) =>
      api(knowledgeOverview, '/v1/knowledge/distinct', { method: 'POST', json: body }),
    onSuccess: done,
  });
  return { tap, edit, readEverything, merge, distinct };
}

/** One question. Slow — the model looks before it answers — and never retried. */
export function useAsk() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AskQuestionBody) => api(answer, '/v1/ask', { method: 'POST', json: body }),
    retry: false,
    onSuccess: (data) => {
      client.setQueryData(['answers', data.id], data);
      void client.invalidateQueries({ queryKey: ['answers', 'list'] });
    },
  });
}

export function useAnswer(id: string | null) {
  return useQuery({
    queryKey: ['answers', id] as const,
    queryFn: () => api(answer, `/v1/answers/${id}`),
    enabled: id !== null,
  });
}

export function useAnswers(query: AnswersQuery) {
  return useQuery({
    queryKey: ['answers', 'list', query] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (query.scope) params.set('scope', query.scope);
      if (query.scopeId) params.set('scopeId', query.scopeId);
      if (query.kept) params.set('kept', query.kept);
      const qs = params.toString();
      return api(z.array(answer), `/v1/answers${qs ? `?${qs}` : ''}`);
    },
  });
}

export function useKeepAnswer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, keep }: { id: string; keep: boolean }) =>
      api(answer, `/v1/answers/${id}/${keep ? 'keep' : 'unkeep'}`, { method: 'POST' }),
    onSuccess: (data) => {
      client.setQueryData(['answers', data.id], data);
      void client.invalidateQueries({ queryKey: ['answers', 'list'] });
    },
  });
}

/** Before a meeting: composed at read time from what this person can open. */
export function useEventBrief(eventId: string) {
  return useQuery({
    queryKey: ['calendar', 'brief', eventId] as const,
    queryFn: () => api(eventBrief, `/v1/calendar/events/${eventId}/brief`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    // The meeting page asks for a brief only while the meeting has not
    // started, and a meeting with no calendar event behind it has none at all.
    enabled: eventId !== '',
  });
}

// --- topics (PRD 30 §3) ---------------------------------------------------------

export function useDiscussions() {
  return useQuery({
    queryKey: ['discussions'] as const,
    queryFn: () => api(discussionsList, '/v1/discussions'),
  });
}

/** One topic; polls while the write-up is being written. */
export function useDiscussion(id: string) {
  return useQuery({
    queryKey: ['topics', id] as const,
    queryFn: () => api(discussionDetail, `/v1/discussions/${id}`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    refetchInterval: (q) => (q.state.data?.status === 'open' ? 5_000 : false),
  });
}

export function useDiscussionActions() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['discussions'] });
    void client.invalidateQueries({ queryKey: ['knowledge'] });
  };
  const writeUp = useMutation({
    mutationFn: (id: string) =>
      api(discussionDetail, `/v1/discussions/${id}/write-up`, { method: 'POST' }),
    onSuccess: done,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) =>
      api(discussionsList, `/v1/discussions/${id}/dismiss`, { method: 'POST' }),
    onSuccess: done,
  });
  return { writeUp, dismiss };
}

export function useServerNotices() {
  return useQuery({
    queryKey: keys.notices,
    queryFn: () => api(z.array(notice), '/v1/notices'),
    refetchInterval: 30_000,
  });
}

export function useMarkNoticeSeen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(z.void(), `/v1/notices/${id}/seen`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.notices }),
  });
}

export function useSpaces() {
  return useQuery({
    queryKey: keys.spaces,
    queryFn: () => api(z.array(spaceSummary), '/v1/spaces'),
  });
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.object({ status: z.string() }), '/v1/auth/logout', { method: 'POST' }),
    onSettled: () => client.clear(),
  });
}

// --- meetings, the week ahead, live bots, series ------------------------------
import { meetingSummary } from '@contracts/meetings';
import type { CorrectLineBody, MeetingsQuery, TranscriptLine } from '@contracts/meetings';
import { upcomingEvent } from '@contracts/calendar';
import { botSent, liveBot } from '@contracts/bots';
import { seriesJoined, seriesSettings, seriesSummary } from '@contracts/series';
import type { SeriesSettingsBody } from '@contracts/series';

const qs = (query: Record<string, string | number | undefined>): string => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query))
    if (v !== undefined && v !== '') params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
};

export function useMeetings(query: MeetingsQuery = {}) {
  return useQuery({
    queryKey: ['meetings', query] as const,
    queryFn: () => api(z.array(meetingSummary), `/v1/meetings${qs(query)}`),
  });
}

/** The calendar's own state: connected, Recall's word for it, last synced. */
export function useCalendar() {
  return useQuery({
    queryKey: ['calendar'] as const,
    queryFn: () => api(calendarStatus, '/v1/calendar'),
  });
}

export function useConnectCalendar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(calendarStatus, '/v1/calendar/connect', { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['calendar'] });
      void client.invalidateQueries({ queryKey: keys.me });
      void client.invalidateQueries({ queryKey: ['upcoming'] });
    },
  });
}

export function useDisconnectCalendar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.void(), '/v1/calendar', { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['calendar'] });
      void client.invalidateQueries({ queryKey: keys.me });
      void client.invalidateQueries({ queryKey: ['upcoming'] });
    },
  });
}

export function useUpcoming(days = 7) {
  return useQuery({
    queryKey: ['upcoming', days] as const,
    queryFn: () => api(z.array(upcomingEvent), `/v1/calendar/events${qs({ days })}`),
    refetchInterval: 60_000,
  });
}

/** A window of the calendar, past included — a week of the Calendar (PRD 67). */
export function useCalendarWindow(from: string, to: string) {
  return useQuery({
    queryKey: ['upcoming', 'window', from, to] as const,
    queryFn: () => api(z.array(upcomingEvent), `/v1/calendar/events${qs({ from, to })}`),
    refetchInterval: 60_000,
  });
}

export function useLiveBots() {
  return useQuery({
    queryKey: ['bots', 'live'] as const,
    queryFn: () => api(z.array(liveBot), '/v1/bots/live'),
    refetchInterval: 15_000,
  });
}

/**
 * Pause, resume, stop.
 *
 * ⚠ `['meeting']` is in here because it was NOT, and that was the whole of
 * "the controls work very bad, after clicking the buttons they still show
 * previous state and then switch it after some time" (Vladimir, 2026-09-21).
 * The meeting's own page — the page the buttons are on — was never told to
 * re-read itself, so it sat on the old state until its ten-second poll came
 * round. The server already knows: pause and resume write the health before
 * they answer.
 */
export function useBotAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, action }: { runId: string; action: 'stop' | 'pause' | 'resume' }) =>
      api(z.void(), `/v1/bots/${runId}/${action}`, { method: 'POST' }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['bots'] });
      void client.invalidateQueries({ queryKey: ['upcoming'] });
      void client.invalidateQueries({ queryKey: ['meeting'] });
      // ⚠ `['brief']` matches nothing — the brief's key is
      // ['calendar', 'brief', id]. It was written the other way and quietly
      // invalidated nothing at all.
      void client.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

/**
 * "Send the notetaker" — now, whatever the clock says (PRD 42).
 *
 * Invalidates the brief as well as the bot list, because the brief is the page
 * the button is on and its `capture` is what turns into the live controls.
 */
export function useSendBot() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => api(botSent, `/v1/bots/send/${eventId}`, { method: 'POST' }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['bots'] });
      void client.invalidateQueries({ queryKey: ['upcoming'] });
      void client.invalidateQueries({ queryKey: ['calendar'] });
      void client.invalidateQueries({ queryKey: ['meeting'] });
    },
  });
}

export function useSeriesList() {
  return useQuery({
    queryKey: ['series'] as const,
    queryFn: () => api(z.array(seriesSummary), '/v1/series'),
  });
}

export function useSeriesSettings(key: string | null) {
  return useQuery({
    queryKey: ['series', key] as const,
    queryFn: () => api(seriesSettings, `/v1/series/${encodeURIComponent(key!)}`),
    enabled: key !== null,
  });
}

/** Two series that are one (PRD 60): `from` is filed under `into` from now on. */
export function useJoinSeries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ from, into }: { from: string; into: string }) =>
      api(seriesJoined, `/v1/series/${encodeURIComponent(from)}/join`, {
        method: 'POST',
        json: { into },
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['series'] });
      void client.invalidateQueries({ queryKey: ['meetings'] });
      void client.invalidateQueries({ queryKey: ['meeting'] });
      void client.invalidateQueries({ queryKey: ['upcoming'] });
      void client.invalidateQueries({ queryKey: ['open'] });
    },
  });
}

export function useUpdateSeries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }: { key: string; body: SeriesSettingsBody }) =>
      api(seriesSettings, `/v1/series/${encodeURIComponent(key)}`, { method: 'PUT', json: body }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['series'] });
      void client.invalidateQueries({ queryKey: ['upcoming'] });
      // Turning a series off changes every one of its meetings' pages.
      void client.invalidateQueries({ queryKey: ['meeting'] });
    },
  });
}

// --- one meeting: reading, writing, sharing, and the ledger's ends ---------------
import {
  chatMessage,
  presenceEvent,
  decision,
  meetingChapter,
  meetingDetail,
  meetingSharing,
  rewriteAnswer,
  seriesSuggestion,
  transcriptLine,
} from '@contracts/meetings';
import type { LinkAccess } from '@contracts/common';
import type { Decision, MeetingDetail, UpdateMeetingBody } from '@contracts/meetings';
import { openItem } from '@contracts/ledger';
import type { AssignOwnerBody, CloseCommitmentBody, CreateCommitmentBody } from '@contracts/ledger';

export const meetingKeys = {
  detail: (id: string) => ['meeting', id] as const,
  transcript: (id: string) => ['meeting', id, 'transcript'] as const,
  walkthrough: (id: string) => ['meeting', id, 'walkthrough'] as const,
  sharing: (id: string) => ['meeting', id, 'sharing'] as const,
};

/**
 * One meeting, which since PRD 43 may be one that is happening right now.
 *
 * While the notetaker is in the call the page re-reads itself every ten
 * seconds: the state it shows — joining, waiting to be let in, recording,
 * paused — is the bot's, and it changes without anybody pressing anything.
 * Once it is over there is nothing left to poll for.
 */
export function useMeeting(id: string, opts: { original?: boolean } = {}) {
  return useQuery({
    // ⚠ The originals under a key of their own (PRD 57): the translated page
    // is what every mutation writes back into, and the two must never be
    // mistaken for each other in the cache.
    queryKey: opts.original ? [...meetingKeys.detail(id), 'original'] : meetingKeys.detail(id),
    queryFn: () => api(meetingDetail, `/v1/meetings/${id}${opts.original ? '?original=1' : ''}`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    // Switching between the translation and the original keeps the page on
    // screen while the other arrives — never for a different meeting.
    placeholderData: (previous) => (previous?.id === id ? previous : undefined),
    refetchInterval: (q) => {
      const meeting = q.state.data;
      if (!meeting) return false;
      if (LIVE_ON_SCREEN.includes(meeting.captureHealth)) return 10_000;
      // ⚠ And while anything is still being made from it. The note is written
      // about forty seconds after the last word, the ledger and the
      // walkthrough after that; stopping the moment the bot left meant "after
      // the recording I needed a reload of the page to see the full note"
      // (Vladimir, 2026-09-21). Ends when there is nothing left to wait for.
      const p = meeting.processing;
      if (p.note || p.ledger || p.walkthrough) return 8_000;
      // A translation on its way is a few seconds of work (PRD 57).
      if (meeting.translation?.pending) return 5_000;
      return false;
    },
  });
}

/** The states worth re-reading a meeting page for. */
const LIVE_ON_SCREEN = ['SCHEDULED', 'JOINING', 'WAITING_ROOM', 'RECORDING', 'PAUSED'];

/** Fixing one line — the words, the speaker — for editors; the transcript in the cache follows. */
export function useCorrectLine(meetingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, body }: { segmentId: string; body: CorrectLineBody }) =>
      api(transcriptLine, `/v1/meetings/${meetingId}/transcript/${segmentId}`, {
        method: 'PATCH',
        json: body,
      }),
    onSuccess: (line) => {
      client.setQueryData<TranscriptLine[]>(meetingKeys.transcript(meetingId), (old) =>
        old?.map((l) => (l.id === line.id ? line : l)),
      );
      void client.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) });
    },
  });
}

/**
 * The transcript — which, while a meeting is still going, grows as people talk
 * (PRD 43).
 *
 * Polled every five seconds rather than pushed. A socket would be nicer to
 * describe and worse to own: it needs a connection held open through Traefik
 * per viewer, a reconnect path, and something to do about the two api replicas
 * a viewer might be balanced between. Five seconds is inside the delay of the
 * transcription itself, which is the real floor on how fresh this can be.
 */
/** A pushed line, or nothing if what arrived is not one. */
function lineOf(data: string): TranscriptLine | null {
  try {
    const parsed = transcriptLine.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * The transcript — and, while the meeting is happening, each new line the
 * moment it is written (PRD 66).
 *
 * Vladimir, 2026-10-08: *"I want them to be quicker not once per 5 seconds"*
 * and *"the problem is not in the recall, it's how often transcription page
 * request updates"*. The server pushes every line down one open connection
 * (`/live`, Server-Sent Events) and the page adds it to what it holds. Each
 * time that connection opens the whole transcript is read once, so a line
 * written while it was down is never lost; while it is down, the page asks
 * every two seconds instead.
 */
export function useTranscript(id: string, enabled: boolean, live = false) {
  const client = useQueryClient();
  const streaming = enabled && live;
  const [pushing, setPushing] = useState(false);
  // Lines pushed since the last read of the whole transcript: what a read
  // already on its way when they arrived could not include.
  const pushed = useRef<TranscriptLine[]>([]);

  useEffect(() => {
    if (!streaming || typeof EventSource === 'undefined') return;
    const key = meetingKeys.transcript(id);
    let source: EventSource | null = null;
    let again: ReturnType<typeof setTimeout> | undefined;
    let wait = 5_000;
    const open = (): void => {
      const stream = new EventSource(`${API_ORIGIN}/v1/meetings/${id}/live`, {
        withCredentials: true,
      });
      source = stream;
      stream.onopen = () => {
        wait = 5_000;
        setPushing(true);
        void client.invalidateQueries({ queryKey: key, exact: true });
      };
      stream.addEventListener('line', (event: MessageEvent<string>) => {
        const line = lineOf(event.data);
        if (!line) return;
        pushed.current.push(line);
        // Before the first read has answered there is nothing to add to; that
        // read keeps the line itself (`withNewer`).
        client.setQueryData<TranscriptLine[]>(key, (held) => (held ? withLine(held, line) : held));
      });
      stream.onerror = () => {
        setPushing(false);
        // CONNECTING: the browser is already on its way back, a second from
        // now. CLOSED: it gave up — an expired session answers 401 — and the
        // two-second read refreshes the session before this tries again;
        // longer each time, up to a minute, for a tab nobody is looking at.
        if (stream.readyState === EventSource.CLOSED) {
          again = setTimeout(open, wait);
          wait = Math.min(wait * 2, 60_000);
        }
      };
    };
    open();
    return () => {
      clearTimeout(again);
      source?.close();
      setPushing(false);
      pushed.current = [];
    };
  }, [client, id, streaming]);

  // Over: the recording's own transcript has replaced the live lines, and it
  // is read once, whole.
  const wasLive = useRef(live);
  useEffect(() => {
    if (wasLive.current && !live) {
      void client.invalidateQueries({ queryKey: meetingKeys.transcript(id), exact: true });
    }
    wasLive.current = live;
  }, [client, id, live]);

  return useQuery({
    queryKey: meetingKeys.transcript(id),
    queryFn: async () => {
      const fresh = await api(z.array(transcriptLine), `/v1/meetings/${id}/transcript`);
      if (!streaming) return fresh;
      const all = withNewer(fresh, pushed.current);
      pushed.current = all.slice(fresh.length);
      return all;
    },
    enabled,
    retry: false,
    refetchInterval: live ? (pushing ? 30_000 : 2_000) : false,
  });
}

/**
 * The meeting retold in order (PRD 47).
 *
 * Its own query, not part of the detail: it is long, and the detail payload is
 * already the biggest thing the app fetches. Written once after the meeting,
 * so this is a read and never a wait.
 */
/**
 * What people typed in the call (PRD 50).
 *
 * Its own read, and only when the transcript is readable: it carries the
 * transcript's access rule, so asking for it as a link reader is a 404 and
 * there is no point spending the request.
 */
export function useMeetingChat(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['meeting-chat', id] as const,
    queryFn: () => api(z.array(chatMessage), `/v1/meetings/${id}/chat`),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Who came into the call and who left it (2026-09-25) — the transcript's
 * access rule, so it is asked for only when the transcript is readable. During
 * the call it follows the room, slower than the words: arrivals are rarer.
 */
export function useMeetingPresence(id: string, enabled: boolean, live = false) {
  return useQuery({
    queryKey: ['meeting-presence', id] as const,
    queryFn: () => api(z.array(presenceEvent), `/v1/meetings/${id}/presence`),
    enabled,
    staleTime: 30_000,
    refetchInterval: live ? 15_000 : false,
  });
}

export function useWalkthrough(
  id: string,
  enabled: boolean,
  opts: { original?: boolean; waiting?: boolean } = {},
) {
  return useQuery({
    queryKey: opts.original
      ? [...meetingKeys.walkthrough(id), 'original']
      : meetingKeys.walkthrough(id),
    queryFn: () =>
      api(
        z.array(meetingChapter),
        `/v1/meetings/${id}/walkthrough${opts.original ? '?original=1' : ''}`,
      ),
    enabled,
    // The other language's chapters while this one's arrive — never another meeting's.
    placeholderData: (previous, query) => (query?.queryKey[1] === id ? previous : undefined),
    // While the meeting says its translation is still on its way (PRD 57).
    refetchInterval: opts.waiting ? 5_000 : false,
  });
}

export function useUpdateMeeting(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeetingBody) =>
      api(meetingDetail, `/v1/meetings/${id}`, { method: 'PATCH', json: body }),
    onSuccess: (detail) => {
      client.setQueryData(meetingKeys.detail(id), detail);
      void client.invalidateQueries({ queryKey: ['meetings'] });
      // ⚠ And the series list. Attaching a meeting is what CREATES a series
      // the client has never heard of — without this the menu still holds the
      // old list, cannot find the new key in it, and shows the raw key
      // (`own:f2cadfd6-…`) where its name should be.
      void client.invalidateQueries({ queryKey: ['series'] });
    },
  });
}

/**
 * The full note, kept as it is typed — the editor saves a moment after the
 * typing stops (`editor/NoteEditor`).
 *
 * ⚠ A refetch already on its way when the save lands carries the words from
 * before it, and would put them back on the page: cancelled first, then the
 * answer written in. Both views of the meeting are kept right — the reader's
 * language, which is what the save answers with, and the originals, which is
 * the one a translated note is edited in (PRD 57).
 */
export function useSaveNote(id: string, field: 'polishedNotes' | 'notes' = 'polishedNotes') {
  const client = useQueryClient();
  // The full note is gone when it is empty; what was typed is an empty string.
  const valueOf = (markdown: string) =>
    field === 'polishedNotes' ? (markdown.trim() ? markdown : null) : markdown;
  return useMutation({
    mutationFn: (markdown: string) =>
      api(meetingDetail, `/v1/meetings/${id}`, {
        method: 'PATCH',
        json: { [field]: valueOf(markdown) },
      }),
    onSuccess: async (detail, markdown) => {
      const translated = meetingKeys.detail(id);
      const original = [...translated, 'original'] as const;
      await Promise.all([
        client.cancelQueries({ queryKey: translated, exact: true }),
        client.cancelQueries({ queryKey: original, exact: true }),
      ]);
      client.setQueryData(translated, detail);
      client.setQueryData<MeetingDetail>(original, (was) =>
        was ? { ...was, [field]: valueOf(markdown) } : was,
      );
      // The list rows' lead is the note's first sentence: stale, not refetched per keystroke.
      void client.invalidateQueries({ queryKey: ['meetings'], refetchType: 'none' });
    },
  });
}

/**
 * The full note written again, from the transcript and what was typed (PRD
 * 62) — replacing it. The meeting is read again at once, so the page moves to
 * "being written again" and brings the new note in when it lands.
 */
export function useRewriteNote(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(rewriteAnswer, `/v1/meetings/${id}/note/rewrite`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: meetingKeys.detail(id) });
    },
  });
}

/**
 * ✦ Rewrite — ask again for whatever of the write-up has not arrived. The
 * meeting is re-read at once, so the page moves from "taking too long" back
 * to "writing up…" and brings each piece in as it lands.
 */
export function useRewrite(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(rewriteAnswer, `/v1/meetings/${id}/rewrite`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: meetingKeys.detail(id) });
    },
  });
}

/** Deletes the meeting for everyone — note, transcript, video, and what was derived from them. */
export function useDeleteMeeting(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.void(), `/v1/meetings/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      client.removeQueries({ queryKey: meetingKeys.detail(id) });
      // Nearly every list on every page counted it: the ledger, a person, a
      // series, knowledge. Cheaper to re-read than to enumerate.
      void client.invalidateQueries();
    },
  });
}

export function useSharing(id: string, enabled: boolean) {
  return useQuery({
    queryKey: meetingKeys.sharing(id),
    queryFn: () => api(meetingSharing, `/v1/meetings/${id}/sharing`),
    enabled,
  });
}

/** Every share change reads the sharing back, so the modal shows the server's truth. */
export function useShareActions(id: string) {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: meetingKeys.sharing(id) });
    void client.invalidateQueries({ queryKey: meetingKeys.detail(id) });
  };
  const grant = useMutation({
    mutationFn: (body: { email: string; role: 'VIEWER' | 'COLLABORATOR' }) =>
      api(z.void(), `/v1/meetings/${id}/shares`, { method: 'POST', json: body }),
    onSuccess: done,
  });
  const revoke = useMutation({
    mutationFn: (email: string) =>
      api(z.void(), `/v1/meetings/${id}/shares/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const addToSpace = useMutation({
    mutationFn: (spaceId: string) =>
      api(z.void(), `/v1/meetings/${id}/spaces`, { method: 'POST', json: { spaceId } }),
    onSuccess: done,
  });
  const removeFromSpace = useMutation({
    mutationFn: (spaceId: string) =>
      api(z.void(), `/v1/meetings/${id}/spaces/${spaceId}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const link = useMutation({
    mutationFn: (linkAccess: LinkAccess) =>
      api(meetingSharing, `/v1/meetings/${id}/link`, { method: 'PUT', json: { linkAccess } }),
    onSuccess: done,
  });
  const fileInFolder = useMutation({
    mutationFn: (folderId: string) =>
      api(z.void(), `/v1/meetings/${id}/folders`, { method: 'POST', json: { folderId } }),
    onSuccess: () => {
      done();
      void client.invalidateQueries({ queryKey: ['folders'] });
    },
  });
  const removeFromFolder = useMutation({
    mutationFn: (folderId: string) =>
      api(z.void(), `/v1/meetings/${id}/folders/${folderId}`, { method: 'DELETE' }),
    onSuccess: () => {
      done();
      void client.invalidateQueries({ queryKey: ['folders'] });
    },
  });
  return { grant, revoke, addToSpace, removeFromSpace, link, fileInFolder, removeFromFolder };
}

/** The ends of a commitment. Every one invalidates the meeting and Open. */
import { commitmentDetail } from '@contracts/ledger';

/**
 * An action written down by a person on the meeting's page (2026-09-23) —
 * everything that reads the ledger re-reads, so it is in the meeting, Open,
 * and the Slack dialog at once.
 */
export function useCreateCommitment(meetingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCommitmentBody) =>
      api(openItem, `/v1/meetings/${meetingId}/commitments`, { method: 'POST', json: body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['meeting'] });
      void client.invalidateQueries({ queryKey: ['open'] });
      void client.invalidateQueries({ queryKey: ['people'] });
      void client.invalidateQueries({ queryKey: ['slack', 'draft', meetingId] });
    },
  });
}

export function useLedgerActions() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['meeting'] });
    void client.invalidateQueries({ queryKey: ['open'] });
    void client.invalidateQueries({ queryKey: ['people'] });
    void client.invalidateQueries({ queryKey: ['commitment'] });
    // What Post to Slack offers is these same rows — handed over, dropped or
    // done, it must not open on the copy from before (2026-09-28).
    void client.invalidateQueries({ queryKey: ['slack', 'draft'] });
  };
  // ⚠ Remembered as the server answered, BEFORE the lists are read again: the
  // row stays where it was, crossed out, with Undo in reach, until the window
  // loses focus (2026-09-25 — `ledger/acted-here`).
  const close = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CloseCommitmentBody }) =>
      api(openItem, `/v1/commitments/${id}/close`, { method: 'POST', json: body }),
    onSuccess: (item) => {
      actedOn(item);
      done();
    },
  });
  const reopen = useMutation({
    mutationFn: (id: string) => api(openItem, `/v1/commitments/${id}/reopen`, { method: 'POST' }),
    onSuccess: (item) => {
      actedOn(item);
      done();
    },
  });
  const assign = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssignOwnerBody }) =>
      api(openItem, `/v1/commitments/${id}/owner`, { method: 'POST', json: body }),
    onSuccess: done,
  });
  /** "Yes, still on" — out of the stale band for another thirty days, and not closed. */
  const keep = useMutation({
    mutationFn: (id: string) =>
      api(commitmentDetail, `/v1/commitments/${id}`, { method: 'PATCH', json: { kept: true } }),
    onSuccess: done,
  });
  return { close, reopen, assign, keep };
}

// --- Open — the ledger ----------------------------------------------------------
import { openLedger } from '@contracts/ledger';
import type { OpenQuery, UpdateCommitmentBody } from '@contracts/ledger';

/** The commitment page in one read. */
export function useCommitment(id: string, opts: { original?: boolean } = {}) {
  return useQuery({
    queryKey: opts.original ? ['commitment', id, 'original'] : ['commitment', id],
    queryFn: () =>
      api(commitmentDetail, `/v1/commitments/${id}${opts.original ? '?original=1' : ''}`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    placeholderData: (previous) => (previous?.item.id === id ? previous : undefined),
    // A translation on its way is a few seconds of work (PRD 57).
    refetchInterval: (q) => (q.state.data?.translation?.pending ? 5_000 : false),
  });
}

/** The wording, by an editor of the meeting. */
export function useUpdateCommitment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCommitmentBody }) =>
      api(commitmentDetail, `/v1/commitments/${id}`, { method: 'PATCH', json: body }),
    onSuccess: (data) => {
      client.setQueryData(['commitment', data.item.id], data);
      void client.invalidateQueries({ queryKey: ['meeting'] });
      void client.invalidateQueries({ queryKey: ['open'] });
    },
  });
}

// --- Asana — a commitment becomes a task (PRD 51) -------------------------------
import { asanaRef, asanaStatus, convertedTask } from '@contracts/asana';

/** Whether this person has connected Asana, and whose account it is. */
export function useAsana() {
  return useQuery({
    queryKey: ['asana'] as const,
    queryFn: () => api(asanaStatus, '/v1/asana'),
    staleTime: 60_000,
  });
}

/** Their workspaces — only asked for once the picker is open. */
export function useAsanaWorkspaces(enabled: boolean) {
  return useQuery({
    queryKey: ['asana', 'workspaces'] as const,
    queryFn: () => api(asanaRef.array(), '/v1/asana/workspaces'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** …and the projects in one. */
export function useAsanaProjects(workspaceGid: string | null) {
  return useQuery({
    queryKey: ['asana', 'projects', workspaceGid] as const,
    queryFn: () => api(asanaRef.array(), `/v1/asana/projects?workspace=${workspaceGid ?? ''}`),
    enabled: !!workspaceGid,
    staleTime: 5 * 60_000,
  });
}

export function useAsanaActions() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['asana'] });
  };
  const connect = useMutation({
    mutationFn: (token: string) =>
      api(asanaStatus, '/v1/asana/token', { method: 'POST', json: { token } }),
    onSuccess: (data) => {
      client.setQueryData(['asana'], data);
      done();
    },
  });
  const disconnect = useMutation({
    mutationFn: () => api(z.void(), '/v1/asana', { method: 'DELETE' }),
    onSuccess: done,
  });
  /** ⚠ Invalidates the ledger too: the commitment has just left it. */
  const convert = useMutation({
    mutationFn: ({
      id,
      workspaceGid,
      projectGid,
    }: {
      id: string;
      workspaceGid: string;
      projectGid: string | null;
    }) =>
      api(convertedTask, `/v1/commitments/${id}/asana`, {
        method: 'POST',
        json: { workspaceGid, projectGid },
      }),
    onSuccess: () => {
      done();
      void client.invalidateQueries({ queryKey: ['open'] });
      void client.invalidateQueries({ queryKey: ['meeting'] });
      void client.invalidateQueries({ queryKey: ['commitment'] });
    },
  });
  return { connect, disconnect, convert };
}

// --- Correcting what a meeting produced (PRD 54) --------------------------------
import type { EditRecordBody } from '@contracts/records';

type RecordKindPath = 'risks' | 'questions' | 'decisions';

/**
 * Editing, re-grading and deleting a meeting's records.
 *
 * ⚠ Deleting is not a status. Vladimir: *"we need to be able to change/delete
 * risks, actions, decisions, questions as they can be wrongly created"* — a
 * record the model invented was never true, and marking it "dropped" would
 * leave the list with a permanent memory of something that never happened.
 */
/*
 * ⚠ `z.void()` for every route that answers 204 with no body. `api()` hands
 * an empty body to the schema as `undefined`, and these used `z.null()` — so
 * each of them succeeded at the server and then reported *"expected null,
 * received undefined"* to the person, without refreshing the page. Rewording,
 * re-statusing and deleting a record, refusing a series suggestion and
 * disconnecting Asana all did it (Vladimir, 2026-09-23, on "Not this one").
 */
export function useRecordActions() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['meeting'] });
    void client.invalidateQueries({ queryKey: ['open'] });
  };
  const edit = useMutation({
    mutationFn: ({ kind, id, body }: { kind: RecordKindPath; id: string; body: EditRecordBody }) =>
      api(z.void(), `/v1/${kind}/${id}`, { method: 'PATCH', json: body }),
    onSuccess: done,
  });
  const setStatus = useMutation({
    mutationFn: ({
      kind,
      id,
      status,
    }: {
      kind: 'risks' | 'questions';
      id: string;
      status: string;
    }) => api(z.void(), `/v1/${kind}/${id}/status`, { method: 'POST', json: { status } }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: ({ kind, id }: { kind: RecordKindPath; id: string }) =>
      api(z.void(), `/v1/${kind}/${id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  return { edit, setStatus, remove };
}

// --- Topics — the subjects one person keeps coming back to (PRD 52) -------------
import { topicCandidates, topicDetail, topicMerged, topicsList } from '@contracts/topics';
import type { MergeTopicBody, RenameTopicBody } from '@contracts/topics';

/**
 * The series this meeting looks like an instance of (PRD 53).
 *
 * ⚠ Its own query, deliberately. It was a field on the meeting once, and the
 * meeting page then waited on several extra reads before it could paint its
 * header — enough to break a smoke story that opens the series menu the moment
 * the page is up. A banner most meetings never show must not be on the
 * critical path of every meeting that does not.
 */
export function useSeriesSuggestion(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['series-suggestion', id] as const,
    queryFn: () => api(seriesSuggestion, `/v1/meetings/${id}/series-suggestion`),
    enabled,
    staleTime: 60_000,
  });
}

/** "No, this meeting is not one of those" (PRD 53). */
export function useRefuseSeries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, seriesKey }: { id: string; seriesKey: string }) =>
      api(z.void(), `/v1/meetings/${id}/series-suggestion/refuse`, {
        method: 'POST',
        json: { seriesKey },
      }),
    onSuccess: (_r, { id }) => {
      void client.invalidateQueries({ queryKey: ['series-suggestion', id] });
    },
  });
}

export function useTopics() {
  return useQuery({
    queryKey: ['topics'] as const,
    queryFn: () => api(topicsList, '/v1/topics'),
    staleTime: 60_000,
  });
}

export function useTopic(id: string) {
  return useQuery({
    queryKey: ['topic', id] as const,
    queryFn: () => api(topicDetail, `/v1/topics/${id}`),
    enabled: !!id,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

/** The person's other threads this one might be the same work as — closest first. */
export function useTopicCandidates(id: string) {
  return useQuery({
    queryKey: ['topic-candidates', id] as const,
    queryFn: () => api(topicCandidates, `/v1/topics/${id}/candidates`),
    enabled: !!id,
  });
}

/**
 * "Same work as…" — fold this thread into another. The folded one is gone, so
 * everything that listed it is read again.
 */
export function useMergeTopic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MergeTopicBody }) =>
      api(topicMerged, `/v1/topics/${id}/merge`, { method: 'POST', json: body }),
    onSuccess: (left, { id }) => {
      client.removeQueries({ queryKey: ['topic', id] });
      client.removeQueries({ queryKey: ['topic-candidates'] });
      void client.invalidateQueries({ queryKey: ['topic', left.id] });
      void client.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

/** A person's own name for their thread (2026-09-24). */
export function useRenameTopic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RenameTopicBody }) =>
      api(topicMerged, `/v1/topics/${id}`, { method: 'PATCH', json: body }),
    onSuccess: (_done, { id }) => {
      void client.invalidateQueries({ queryKey: ['topic', id] });
      void client.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

export function useOpen(query: OpenQuery = {}) {
  return useQuery({
    queryKey: ['open', query] as const,
    queryFn: () => api(openLedger, `/v1/open${qs(query)}`),
    refetchInterval: 60_000,
  });
}

// --- your settings, the workspace ------------------------------------------------
import { userSettings } from '@contracts/settings';
import type { UpdateSettingsBody } from '@contracts/settings';
import type { TileKind, UpdateOrgBody } from '@contracts/orgs';

export function useSettings() {
  return useQuery({
    queryKey: ['settings'] as const,
    queryFn: () => api(userSettings, '/v1/settings'),
  });
}

export function useUpdateSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSettingsBody) =>
      api(userSettings, '/v1/settings', { method: 'PUT', json: body }),
    onSuccess: (data) => {
      client.setQueryData(['settings'], data);
      void client.invalidateQueries({ queryKey: ['upcoming'] });
      // The meeting page carries the same verdict as the row now, so "record
      // my one-to-ones" changes what a meeting PAGE says too. Without this it
      // went on promising a notetaker the dispatcher would refuse.
      void client.invalidateQueries({ queryKey: ['meeting'] });
      // A series nobody switched either way follows "Join my meetings", so its
      // switch moves with it (2026-10-08).
      void client.invalidateQueries({ queryKey: ['series'] });
    },
  });
}

export function useUpdateOrg() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrgBody) =>
      api(organization, '/v1/org', { method: 'PATCH', json: body }),
    onSuccess: (data) => client.setQueryData(keys.org, data),
  });
}

/** The notetaker's two camera pictures — the workspace's own, or the defaults (2026-09-30). */
export function useOrgTiles() {
  return useQuery({
    queryKey: ['org', 'tiles'] as const,
    queryFn: () => api(orgTiles, '/v1/org/tiles'),
  });
}

/** An administrator's own picture in place of a default — or, with `null`, back to the default. */
export function useSetOrgTile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, image }: { kind: TileKind; image: string | null }) =>
      image === null
        ? api(orgTiles, `/v1/org/tiles/${kind}`, { method: 'DELETE' })
        : api(orgTiles, `/v1/org/tiles/${kind}`, { method: 'PUT', json: { image } }),
    onSuccess: (data) => client.setQueryData(['org', 'tiles'], data),
  });
}

/**
 * Deletes the workspace (its shared spaces' meetings, its settings, its member
 * list). Everything cached is about a workspace that no longer exists, so the
 * cache goes and the app reads the fresh, empty one the server left in its place.
 */
/**
 * The workspace's audit log — administrators only; the server's 404 is a state
 * ("for administrators"), not a failure to retry.
 */
export function useOrgAudit() {
  return useQuery({
    queryKey: [...keys.org, 'audit'] as const,
    queryFn: () => api(z.array(auditEvent), '/v1/org/audit'),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
    staleTime: 30_000,
  });
}

export function useDeleteWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.void(), '/v1/org', { method: 'DELETE' }),
    onSuccess: () => client.clear(),
  });
}

/** Deletes the account and everything under it. There is nobody left to show a page to. */
export function useDeleteAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.void(), '/v1/account', { method: 'DELETE' }),
    onSuccess: () => client.clear(),
  });
}

/** Where the browser saves "everything under my account" from — a plain link, like the video. */
export const ACCOUNT_EXPORT_URL = `${API_ORIGIN}/v1/account/export`;

export function useSetRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'ADMIN' | 'MEMBER' }) =>
      api(organization, `/v1/org/members/${userId}/role`, { method: 'PUT', json: { role } }),
    onSuccess: (data) => client.setQueryData(keys.org, data),
  });
}

// --- people, search, spaces and folders ---------------------------------------------
import { person } from '@contracts/people';
import { searchResults } from '@contracts/search';
import {
  folderSummary,
  spaceDetail,
  spaceMember,
  spaceSummary as spaceSummarySchema,
} from '@contracts/spaces';
import type { CreateFolderBody, CreateSpaceBody, UpdateFolderBody } from '@contracts/spaces';

export function usePeople() {
  return useQuery({
    queryKey: ['people'] as const,
    queryFn: () => api(z.array(person), '/v1/people'),
  });
}

/** The decisions of meetings you were both in — "decisions together". */
export function usePersonDecisions(email: string) {
  return useQuery({
    queryKey: ['people', email, 'decisions'] as const,
    queryFn: () => api(z.array(decision), `/v1/people/${encodeURIComponent(email)}/decisions`),
    enabled: email.length > 0,
  });
}

export function usePerson(email: string) {
  return useQuery({
    queryKey: ['people', email] as const,
    queryFn: () => api(person, `/v1/people/${encodeURIComponent(email)}`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: ['search', q] as const,
    queryFn: () => api(searchResults, `/v1/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
  });
}

export function useSpace(id: string | null) {
  return useQuery({
    queryKey: ['spaces', id] as const,
    queryFn: () => api(spaceDetail, `/v1/spaces/${id!}`),
    enabled: id !== null,
  });
}

/** One folder, for its page. */
export function useFolder(id: string) {
  return useQuery({
    queryKey: ['folders', id] as const,
    queryFn: () => api(folderSummary, `/v1/folders/${id}`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

/** Saving a folder's name, description or rule. */
export function useSaveFolder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFolderBody }) =>
      api(folderSummary, `/v1/folders/${id}`, { method: 'PATCH', json: body }),
    onSuccess: (folder) => {
      client.setQueryData(['folders', folder.id], folder);
      void client.invalidateQueries({ queryKey: ['folders'] });
      void client.invalidateQueries({ queryKey: ['meetings'] });
      void client.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

export function useFolders() {
  return useQuery({
    queryKey: ['folders'] as const,
    queryFn: () => api(z.array(folderSummary), '/v1/folders'),
  });
}

export function useSpaceActions() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['spaces'] });
    void client.invalidateQueries({ queryKey: ['folders'] });
  };
  const createSpace = useMutation({
    mutationFn: (body: CreateSpaceBody) =>
      api(spaceSummarySchema, '/v1/spaces', { method: 'POST', json: body }),
    onSuccess: done,
  });
  const createFolder = useMutation({
    mutationFn: (body: CreateFolderBody) =>
      api(folderSummary, '/v1/folders', { method: 'POST', json: body }),
    onSuccess: done,
  });
  const updateSpace = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(spaceSummarySchema, `/v1/spaces/${id}`, { method: 'PATCH', json: { name } }),
    onSuccess: done,
  });
  const updateFolder = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(folderSummary, `/v1/folders/${id}`, { method: 'PATCH', json: { name } }),
    onSuccess: done,
  });
  const removeFolder = useMutation({
    mutationFn: (id: string) => api(z.void(), `/v1/folders/${id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const addMember = useMutation({
    mutationFn: ({
      spaceId,
      email,
      role,
    }: {
      spaceId: string;
      email: string;
      role: 'OWNER' | 'MEMBER';
    }) =>
      api(z.array(spaceMember), `/v1/spaces/${spaceId}/members`, {
        method: 'POST',
        json: { email, role },
      }),
    onSuccess: done,
  });
  const removeMember = useMutation({
    mutationFn: ({ spaceId, userId }: { spaceId: string; userId: string }) =>
      api(z.void(), `/v1/spaces/${spaceId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  return {
    createSpace,
    createFolder,
    removeFolder,
    addMember,
    removeMember,
    updateSpace,
    updateFolder,
  };
}

// --- connected apps and personal tokens (MCP) --------------------------------------
import { connectedApp, createdMcpToken, mcpToken } from '@contracts/mcp';
import type { CreateMcpTokenBody } from '@contracts/mcp';

export function useConnectedApps() {
  return useQuery({
    queryKey: ['oauth', 'grants'] as const,
    queryFn: () => api(z.array(connectedApp), '/v1/oauth/grants'),
  });
}

export function useDisconnectApp() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(z.void(), `/v1/oauth/grants/${id}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['oauth', 'grants'] }),
  });
}

export function useMcpTokens() {
  return useQuery({
    queryKey: ['mcp', 'tokens'] as const,
    queryFn: () => api(z.array(mcpToken), '/v1/mcp/tokens'),
  });
}

export function useCreateMcpToken() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMcpTokenBody) =>
      api(createdMcpToken, '/v1/mcp/tokens', { method: 'POST', json: body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['mcp', 'tokens'] }),
  });
}

export function useRevokeMcpToken() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(z.void(), `/v1/mcp/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['mcp', 'tokens'] }),
  });
}

// --- one meeting, one answer -----------------------------------------------------------
export function useRecordOverride() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, record }: { eventId: string; record: boolean | null }) =>
      api(z.void(), `/v1/calendar/events/${eventId}/record`, { method: 'PUT', json: { record } }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['upcoming'] });
      // ⚠ The meeting's own page too: skipping cancels a booked bot, and the
      // band on that page is drawn from the meeting's capture state.
      void client.invalidateQueries({ queryKey: ['meeting'] });
    },
  });
}

/** Names for addresses, from the people this person has met. */
export function useNames(): Map<string, string> {
  const people = usePeople();
  const me = useMe();
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const p of people.data ?? []) if (p.name) m.set(p.email.toLowerCase(), p.name);
    if (me.data?.name) m.set(me.data.email.toLowerCase(), me.data.name);
    return m;
  }, [people.data, me.data]);
}

/** The decisions of a few meetings — a thin week's "what the week decided". Capped at ten fetches. */
export function useWeekDecisions(ids: string[]): Decision[] {
  const few = ids.slice(0, 10);
  const results = useQueries({
    queries: few.map((id) => ({
      queryKey: meetingKeys.detail(id),
      queryFn: () => api(meetingDetail, `/v1/meetings/${id}`),
      staleTime: 60_000,
    })),
  });
  // Keyed on the data's own timestamps: `results` is a new array every render.
  const stamp = results.map((r) => r.dataUpdatedAt).join(',');
  const decisions = results.flatMap((r) => r.data?.decisions ?? []);
  return useMemo(() => decisions, [stamp]);
}

// --- bringing a history in from another product (PRD 37) -------------------------------

/**
 * What is in their Fellow. A mutation rather than a query because it carries a
 * key and happens when they press a button — nothing about it should be cached,
 * refetched on a window focus, or kept after the page is left.
 */
export function useFellowPreview() {
  return useMutation({
    mutationFn: (body: FellowPreviewBody) =>
      api(fellowPreview, '/v1/imports/fellow/preview', { method: 'POST', json: body }),
  });
}

export function useStartImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: StartImportBody) =>
      api(importRun, '/v1/imports/fellow', { method: 'POST', json: body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['imports'] });
    },
  });
}

/** One run, while it is running: the progress bar's source. */
export function useImportRun(id: string | null) {
  return useQuery({
    queryKey: ['imports', id] as const,
    queryFn: () => api(importRun, `/v1/imports/${id!}`),
    enabled: id !== null,
    // While it is working, ask again; once it has ended, stop asking.
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'QUEUED' || state === 'RUNNING' ? 2000 : false;
    },
  });
}

export function useImports() {
  return useQuery({
    queryKey: ['imports'] as const,
    queryFn: () => api(z.array(importRun), '/v1/imports'),
  });
}

export function useCancelImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(importRun, `/v1/imports/${id}/cancel`, { method: 'POST' }),
    onSuccess: (run) => {
      client.setQueryData(['imports', run.id], run);
      void client.invalidateQueries({ queryKey: ['imports'] });
    },
  });
}

// --- Slack (PRD 33) --------------------------------------------------------

/** Is Slack connected, and may this person change that. */
export function useSlack() {
  return useQuery({ queryKey: ['slack'] as const, queryFn: () => api(slackState, '/v1/slack') });
}

/**
 * The channels the workspace could post to.
 *
 * ⚠ Only asked for when somebody opens the picker: it is a round trip to
 * Slack, and a page that lists every channel on load is a page that is slow
 * for everybody who was not going to post.
 */
export function useSlackChannels(enabled: boolean) {
  return useQuery({
    queryKey: ['slack', 'channels'] as const,
    queryFn: () => api(z.array(slackChannel), '/v1/slack/channels'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useDisconnectSlack() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.undefined(), '/v1/slack', { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['slack'] }),
  });
}

/** Takes this person's own Slack away — messages stop coming from them (PRD 56 §6). */
export function useDisconnectMySlack() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(z.undefined(), '/v1/slack/me', { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['slack'] }),
  });
}

/** What this meeting would post, and who cannot be tagged. */
export function useSlackDraft(meetingId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['slack', 'draft', meetingId] as const,
    queryFn: () => api(slackDraft, `/v1/meetings/${meetingId}/slack`),
    enabled: enabled && meetingId !== '',
  });
}

/** Each owner, privately: their own chosen actions and the sender's (PRD 56). */
export function useSendSlackDms(meetingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      commitmentIds,
      decisionIds,
      as,
    }: {
      commitmentIds: string[];
      decisionIds: string[];
      as: 'me' | 'bot';
    }) =>
      api(slackDmsSent, `/v1/meetings/${meetingId}/slack/dms`, {
        method: 'POST',
        json: { commitmentIds, decisionIds, as },
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['slack', 'draft', meetingId] });
      void client.invalidateQueries({ queryKey: ['meeting', meetingId] });
    },
  });
}

/** The note, or one write-up, to one person's DM — from your own Slack (2026-10-08). */
export function useSendNoteDm(meetingId: string) {
  return useMutation({
    mutationFn: (body: SlackNoteDmBody) =>
      api(slackNoteDmSent, `/v1/meetings/${meetingId}/slack/note`, { method: 'POST', json: body }),
  });
}

export function usePostToSlack(meetingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SlackPostBody) =>
      api(slackPosted, `/v1/meetings/${meetingId}/slack`, { method: 'POST', json: body }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['slack', 'draft', meetingId] });
      void client.invalidateQueries({ queryKey: ['meeting', meetingId] });
    },
  });
}

// --- Channels — groups of people, and the meetings posted to them (PRD 58) -------
import {
  channelDetail,
  channelMember,
  channelSummary,
  channelsList,
  seriesFollowed,
} from '@contracts/channels';
import type { CreateChannelBody, UpdateChannelBody } from '@contracts/channels';

export function useChannels() {
  return useQuery({
    queryKey: ['channels'] as const,
    queryFn: () => api(channelsList, '/v1/channels'),
  });
}

export function useChannel(id: string) {
  return useQuery({
    queryKey: ['channels', id] as const,
    queryFn: () => api(channelDetail, `/v1/channels/${id}`),
    enabled: !!id,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

/**
 * Everything a channel can be asked to do. Each reads the channel and the
 * lists back afterwards: posting shares a meeting, so what people can see has
 * changed, and every list that counts meetings must hear of it.
 */
export function useChannelActions() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['channels'] });
    void client.invalidateQueries({ queryKey: ['meetings'] });
    void client.invalidateQueries({ queryKey: ['meeting'] });
  };
  const create = useMutation({
    mutationFn: (body: CreateChannelBody) =>
      api(channelSummary, '/v1/channels', { method: 'POST', json: body }),
    onSuccess: done,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateChannelBody }) =>
      api(channelSummary, `/v1/channels/${id}`, { method: 'PATCH', json: body }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(z.void(), `/v1/channels/${id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const join = useMutation({
    mutationFn: (id: string) => api(channelSummary, `/v1/channels/${id}/join`, { method: 'POST' }),
    onSuccess: done,
  });
  const leave = useMutation({
    mutationFn: (id: string) => api(z.void(), `/v1/channels/${id}/leave`, { method: 'POST' }),
    onSuccess: done,
  });
  const addMember = useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      api(z.array(channelMember), `/v1/channels/${id}/members`, {
        method: 'POST',
        json: { email },
      }),
    onSuccess: done,
  });
  const removeMember = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api(z.void(), `/v1/channels/${id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const makeOwner = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api(z.void(), `/v1/channels/${id}/members/${userId}/owner`, { method: 'POST' }),
    onSuccess: done,
  });
  const post = useMutation({
    mutationFn: ({ id, meetingId }: { id: string; meetingId: string }) =>
      api(z.void(), `/v1/channels/${id}/meetings`, { method: 'POST', json: { meetingId } }),
    onSuccess: done,
  });
  const unpost = useMutation({
    mutationFn: ({ id, meetingId }: { id: string; meetingId: string }) =>
      api(z.void(), `/v1/channels/${id}/meetings/${meetingId}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const follow = useMutation({
    mutationFn: ({ id, seriesKey }: { id: string; seriesKey: string }) =>
      api(seriesFollowed, `/v1/channels/${id}/series`, { method: 'POST', json: { seriesKey } }),
    onSuccess: done,
  });
  const unfollow = useMutation({
    mutationFn: ({ id, seriesKey }: { id: string; seriesKey: string }) =>
      api(z.void(), `/v1/channels/${id}/series/${encodeURIComponent(seriesKey)}`, {
        method: 'DELETE',
      }),
    onSuccess: done,
  });
  return {
    create,
    update,
    remove,
    join,
    leave,
    addMember,
    removeMember,
    makeOwner,
    post,
    unpost,
    follow,
    unfollow,
  };
}

// --- templates, and meetings written up with them (PRD 61) -------------------------
import { noteTemplate, writeup } from '@contracts/templates';
import type { TemplateBody, WriteupSlackBody } from '@contracts/templates';

/** Built-in first, then the workspace's, then your own. */
export function useTemplates(enabled = true) {
  return useQuery({
    queryKey: ['templates'] as const,
    queryFn: () => api(z.array(noteTemplate), '/v1/templates'),
    enabled,
    staleTime: 60_000,
  });
}

export function useTemplateActions() {
  const client = useQueryClient();
  const settled = () => void client.invalidateQueries({ queryKey: ['templates'] });
  return {
    create: useMutation({
      mutationFn: (body: TemplateBody) =>
        api(noteTemplate, '/v1/templates', { method: 'POST', json: body }),
      onSettled: settled,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: TemplateBody }) =>
        api(noteTemplate, `/v1/templates/${encodeURIComponent(id)}`, { method: 'PUT', json: body }),
      onSettled: settled,
    }),
    remove: useMutation({
      mutationFn: (id: string) =>
        api(z.void(), `/v1/templates/${encodeURIComponent(id)}`, { method: 'DELETE' }),
      onSettled: settled,
    }),
  };
}

/**
 * A meeting's write-ups — beside its full note, never instead of it. The
 * transcript's reader, so asked for on exactly that condition; while one is
 * being written the list follows it every few seconds.
 */
export function useWriteups(meetingId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['writeups', meetingId] as const,
    queryFn: () => api(z.array(writeup), `/v1/meetings/${meetingId}/writeups`),
    enabled,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((w) => w.status === 'writing') ? 3_000 : false,
  });
}

export function useWriteupActions(meetingId: string) {
  const client = useQueryClient();
  const key = ['writeups', meetingId] as const;
  const settled = () => void client.invalidateQueries({ queryKey: key });
  // ⚠ Into the list the moment the server answers: the page picks the new
  // write-up at once, and a list that has not caught up yet would read as
  // "deleted meanwhile" and throw the reader back to the full note.
  const put = (w: z.infer<typeof writeup>) =>
    client.setQueryData<z.infer<typeof writeup>[]>(key, (was) =>
      was?.some((x) => x.id === w.id)
        ? was.map((x) => (x.id === w.id ? w : x))
        : [...(was ?? []), w],
    );
  const base = `/v1/meetings/${meetingId}/writeups`;
  return {
    apply: useMutation({
      mutationFn: (templateId: string) =>
        api(writeup, base, { method: 'POST', json: { templateId } }),
      onSuccess: put,
      onSettled: settled,
    }),
    rewrite: useMutation({
      mutationFn: (writeupId: string) =>
        api(writeup, `${base}/${writeupId}/rewrite`, { method: 'POST' }),
      onSuccess: put,
      onSettled: settled,
    }),
    remove: useMutation({
      mutationFn: (writeupId: string) =>
        api(z.void(), `${base}/${writeupId}`, { method: 'DELETE' }),
      onSuccess: (_void, writeupId) =>
        client.setQueryData<z.infer<typeof writeup>[]>(key, (was) =>
          was?.filter((x) => x.id !== writeupId),
        ),
      onSettled: settled,
    }),
    slack: useMutation({
      mutationFn: ({ writeupId, body }: { writeupId: string; body: WriteupSlackBody }) =>
        api(writeup, `${base}/${writeupId}/slack`, { method: 'POST', json: body }),
      onSuccess: put,
      onSettled: settled,
    }),
    // Kept as it is typed. ⚠ The list polls while another write-up is being
    // written: a poll already on its way carries the words from before the
    // save, so it is cancelled before the answer goes in.
    edit: useMutation({
      mutationFn: ({ writeupId, body }: { writeupId: string; body: string }) =>
        api(writeup, `${base}/${writeupId}`, { method: 'PATCH', json: { body } }),
      onSuccess: async (w) => {
        await client.cancelQueries({ queryKey: key, exact: true });
        put(w);
      },
    }),
  };
}
